
import humanizeDuration, { Unit } from 'humanize-duration'
import dayjs, { Dayjs } from 'dayjs'
import i18n from './config/i18n'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
dayjs.extend(utc)
dayjs.extend(quarterOfYear)
dayjs.extend(timezone)
dayjs.extend(quarterOfYear)

// eslint-disable-next-line no-shadow
export enum CommonDurationUnits {
  'yearly' = 525960,
  'quarterly' = 131490,
  'monthly' = 43830,
  'weekly' = 10080,
  'daily' = 1440,
  'hourly' = 60
}

export const computeDuration = (
  startAt: Dayjs,
  endAt: Dayjs,
  durationUnit: number,
  tz?: string
): number => {
  let duration: number

  // Set timezone
  const startAtInTz: Dayjs = tz ? dayjs.tz(startAt, tz) : startAt
  const endAtInTz: Dayjs = tz ? dayjs.tz(endAt, tz) : endAt

  // Check if DST and apply grace period if so
  if (startAtInTz.utcOffset() < endAtInTz.utcOffset()) {
    // Summer time (DST)
    startAt = startAt.clone().subtract(60, 'minutes')
  } else if (startAtInTz.utcOffset() > endAtInTz.utcOffset()) {
    // Winter time (DST)
    endAt = endAt.clone().subtract(60, 'minutes')
  }

  switch (durationUnit) {
    case CommonDurationUnits.yearly:
      duration = endAt.clone().add(1, 'days').diff(startAt, 'year')
      break
    case CommonDurationUnits.quarterly:
      duration = endAt.clone().add(1, 'days').diff(startAt, 'quarter')
      break
    case CommonDurationUnits.monthly:
    //  Round the difference to the nearest whole number and convert to a number
      // Ensures that if the calculated difference is 5.93 months, it will be rounded to 6 months
      duration = Number(endAt.clone().add(1, 'days').diff(startAt, 'month', true).toFixed(0))
      break
    case CommonDurationUnits.weekly:
      duration = Number(endAt.clone().add(1, 'days').diff(startAt, 'weeks', true).toFixed(0))
      break

    default:
      duration = Math.ceil(endAt.diff(startAt, 'minutes') / (durationUnit))
      break
  }

  return duration > 1 ? duration : 1
}

export const formatDuration = (
  duration: number,
  durationUnit: number,
  filterOne = false
): string => {
  let units: Unit[] = ['y', 'mo', 'w', 'd', 'h', 'm']
  switch (durationUnit) {
    case CommonDurationUnits.yearly:
      units = ['y']
      break
    case CommonDurationUnits.quarterly:
      units = ['mo']
      break
    case CommonDurationUnits.monthly:
      units = ['mo']
      break
    case CommonDurationUnits.weekly:
      units = ['w']
      break

    default:

      break
  }

  const result = humanizeDuration(duration * durationUnit, {
    unitMeasures: {
      y: CommonDurationUnits.yearly,
      mo: CommonDurationUnits.monthly,
      w: CommonDurationUnits.weekly,
      d: CommonDurationUnits.daily,
      h: CommonDurationUnits.hourly,
      m: 1
    },
    units,
    language: i18n.language,
    fallbacks: ['en']
  })

  if (filterOne && duration === 1 && result.startsWith('1 ')) {
    return result.replace('1 ', '')
  }

  return result
}

export const computeEndDate = (startAt: Dayjs, duration: number, durationUnit:number):Dayjs => {
  switch (durationUnit) {
    case CommonDurationUnits.yearly:
      return startAt.clone().add(duration, 'years').subtract(1, 'days').hour(startAt.hour()).minute(startAt.minute())
    case CommonDurationUnits.quarterly:
      return startAt.clone().add(duration, 'quarters').subtract(1, 'days').hour(startAt.hour()).minute(startAt.minute())
    case CommonDurationUnits.monthly:
      return startAt.clone().add(duration, 'months').subtract(1, 'days').hour(startAt.hour()).minute(startAt.minute())
    case CommonDurationUnits.weekly:

      return startAt.clone().add(duration, 'weeks').hour(startAt.hour()).minute(startAt.minute())

    default:

      return startAt.clone().add(duration * durationUnit, 'minutes')
  }
}
